import { Component, Input, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Party } from '../models/party';
import { PartyType } from '../models/party-type';
import { TranslateService } from '@ngx-translate/core';
import { OnyxEvent } from '../models/event';
import { Contact } from '../models/contact';
import { PropertyLookupStore } from '../../shared/lookup/property-lookup/property-lookup.store';
import { LogService } from '../../shared/log/log.service';
import { PropertyLookupSummary } from '../../shared/models/property-lookup-summary';
import { PropertyLookupDetail } from '../../shared/models/property-lookup-detail';
import { PropertyLookupConverter } from '../utils/property-lookup-converter';
import { AgencyLookupStore } from '../../shared/lookup/agency-lookup/agency-lookup.store';
import { AgencyLookupSummary } from '../../shared/models/agency-lookup-summary';
import { AgencyLookupDetail } from '../../shared/models/agency-lookup-detail';
import { AgencyLookupConverter } from '../utils/agency-lookup-converter';

@Component({
  selector: 'pioneer-contact-lookup-form',
  templateUrl: 'contact-lookup-form.component.html'
})
export class ContactLookupFormComponent implements OnInit, OnChanges {

  @Input('partyType')
  public partyType: PartyType;

  @Input('parentForm')
  public parentForm: FormGroup;

  public contactLookupForm: FormGroup;
  public onyxEvent: OnyxEvent;

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  @Input()
  public isReadOnly = false;

  public contactTypeTitle: string;
  public contactReferenceNumberTitle: string;
  public summaryResults;

  private checkError: boolean;
  private contactFormFilled: boolean;
  private lookupSelected: boolean;

  get agencyStore(): AgencyLookupStore {
    return this.agencyLookupStore;
  }

  get propertyStore(): PropertyLookupStore {
    return this.propertyLookupStore;
  }

  static isPropertyError(group: FormGroup): any {
    if (group.get('partyType').value === PartyType.PROPERTY.toString() &&
      (
        !group.get('propertyId').value || !group.get('chain').value || !group.get('brand').value
      )) {
      return { 'propertyRequired': true };
    } else {
      return null;
    }
  }

  constructor(private formBuilder: FormBuilder,
              private translateService: TranslateService,
              private propertyLookupStore: PropertyLookupStore,
              private agencyLookupStore: AgencyLookupStore,
              private logService: LogService) {
  }

  ngOnInit() {
    this.checkError = false;
    this.contactFormFilled = false;

    if (!this.onyxEvent) {
      this.onyxEvent = new OnyxEvent();
    }

    switch (this.partyType) {
      case PartyType.CLIENT:
        this.translateService.get('EVENT.CONTACT_CLIENT').subscribe(text => this.contactTypeTitle = text);
        this.translateService.get('EVENT.CONTACT_CLIENT_REF_CODE')
          .subscribe(text => this.contactReferenceNumberTitle = text);

        this.contactLookupForm = this.dataToFormGroup(this.onyxEvent.eventClient);
        this.parentForm.setControl('eventClient', this.contactLookupForm);

        break;
      case PartyType.AGENCY:
        this.translateService.get('EVENT.CONTACT_AGENCY').subscribe(text => this.contactTypeTitle = text);
        this.translateService.get('EVENT.CONTACT_AGENCY_REF_CODE')
          .subscribe(text => this.contactReferenceNumberTitle = text);

        this.contactLookupForm = this.dataToFormGroup(this.onyxEvent.eventAgency);
        this.parentForm.setControl('eventAgency', this.contactLookupForm);

        break;
      case PartyType.PROPERTY:
        this.translateService.get('EVENT.CONTACT_PROPERTY').subscribe(text => this.contactTypeTitle = text);
        this.translateService.get('EVENT.CONTACT_PROPERTY_REF_CODE')
          .subscribe(text => this.contactReferenceNumberTitle = text);

        this.contactLookupForm = this.dataToFormGroup(this.onyxEvent.eventProperty);
        this.parentForm.setControl('eventProperty', this.contactLookupForm);
        break;
      default:
        throw Error(`Invalid partyType ${this.partyType}`);
    }

    /*
     Wire up Property Name Search
     */
    if (this.partyType === PartyType.PROPERTY) {
      this.propertyLookupStore.selected.subscribe((details: PropertyLookupDetail) => {
        this.logService.verbose(`property details ${JSON.stringify(details)}`);

        PropertyLookupConverter.updateFormFields(details, this.contactLookupForm, this.propertyLookupStore);
      });
    }

    if (this.partyType === PartyType.AGENCY) {
      this.agencyLookupStore.selected.subscribe((details: AgencyLookupDetail) => {
        this.logService.verbose(`agency details ${JSON.stringify(details)}`);

        AgencyLookupConverter.updateFormFields(details, this.contactLookupForm, this.agencyLookupStore);
      });

    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes[ 'event' ]) {
      return;
    }

    switch (this.partyType) {
      case PartyType.CLIENT:
        this.dataToFormGroup(this.onyxEvent.eventClient);
        break;
      case PartyType.AGENCY:
        this.dataToFormGroup(this.onyxEvent.eventAgency);
        break;
      case PartyType.PROPERTY:
        this.dataToFormGroup(this.onyxEvent.eventProperty);
        break;
      default:
        throw Error(`Invalid partyType ${this.partyType}`);
    }
  }

  inlineSearchTouched(): void {
    // Check for an error if the user has never tried to select a property
    // A delay is required so that this logic is done after onSelectedProperty()
    setTimeout(() => {
      if (this.partyType === PartyType.PROPERTY) {
        this.checkError = !this.contactFormFilled;
      }

      if (!this.lookupSelected) {
        this.propertyLookupStore.clearSearchResults();
        this.agencyLookupStore.clearSearchResults();
      } else {
        this.lookupSelected = false;
      }
    }, 100);
  }

  isPropertyMissing(): boolean {
    return this.checkError && this.contactLookupForm.hasError('propertyRequired');
  }

  isReadyOnlyProperty(): boolean {
    return !this.isReadOnly && this.partyType === PartyType.PROPERTY;
  }

  updateResults(results): void {
    this.logService.verbose(`results: ${results}`);
    this.summaryResults = results;
  }

  onSelectedProperty(propertySummary: PropertyLookupSummary) {
    this.contactFormFilled = true;
    this.lookupSelected = true;
    this.logService.debug(`propertySummary ${JSON.stringify(propertySummary)}`);
    this.propertyLookupStore.getDetail(propertySummary).subscribe();
  }

  onSelectedAgency(summary: AgencyLookupSummary) {
    this.contactFormFilled = true;
    this.lookupSelected = true;
    this.logService.debug(`agencySummary ${JSON.stringify(summary)}`);
    this.agencyLookupStore.getDetail(summary).subscribe();
  }

  private dataToFormGroup(party: Party): FormGroup {

    if (!party) {
      party = new Party();
    } else {
      if (!party.contact) {
        party.contact = new Contact();
      }
      party = new Party(party);
    }

    let refCodeValidator = [ party.eventReferenceNumber, Validators.nullValidator ];

    switch (this.partyType) {
      case PartyType.CLIENT:
        party = party.set('clientReferenceNumber', this.onyxEvent.clientReferenceNumber) as Party;
        break;
      case PartyType.AGENCY:
        party = party.set('agencyId', this.onyxEvent.agencyId) as Party;
        break;
      case PartyType.PROPERTY:

        party = party.merge({
          propertyId: this.onyxEvent.propertyId,
          chain: this.onyxEvent.chain,
          brand: this.onyxEvent.brand
        }) as Party;

        refCodeValidator = [ party.eventReferenceNumber, Validators.nullValidator ];
        break;
      default:
        throw Error(`Invalid partyType ${this.partyType}`);
    }

    if (!this.contactLookupForm) {
      this.contactLookupForm = this.formBuilder.group({
        propertyNameSearch: '',
        agencyNameSearch: '',
        name: party.name,
        line1: party.line1,
        line2: party.line2,
        partyType: this.partyType,
        city: party.city,
        state: party.state,
        postalCode: party.postalCode,
        agencyId: party.agencyId,
        clientReferenceNumber: party.clientReferenceNumber,
        propertyId: party.propertyId,
        chain: party.chain,
        brand: party.brand,
        eventReferenceNumber: refCodeValidator,
        contact: this.formBuilder.group({
          firstName: [ party.contact.firstName ],
          lastName: [ party.contact.lastName ],
          partyType: this.partyType,
          email: [ party.contact.email ],
          phone: [ party.contact.phone ],
          fax: [ party.contact.fax ]
        })
      }, {
        validator: ContactLookupFormComponent.isPropertyError
      });
    } else {
      this.contactLookupForm.patchValue({
        propertyNameSearch: '',
        agencyNameSearch: '',
        name: party.name,
        line1: party.line1,
        line2: party.line2,
        partyType: this.partyType,
        city: party.city,
        state: party.state,
        postalCode: party.postalCode,
        agencyId: party.agencyId,
        clientReferenceNumber: party.clientReferenceNumber,
        propertyId: party.propertyId,
        chain: party.chain,
        brand: party.brand,
        eventReferenceNumber: party.eventReferenceNumber,
      });
      this.contactLookupForm.controls[ 'contact' ].patchValue({
        firstName: party.contact.firstName,
        lastName: party.contact.lastName,
        partyType: this.partyType,
        email: party.contact.email,
        phone: party.contact.phone,
        fax: party.contact.fax
      });
    }

    return this.contactLookupForm;
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/contact-lookup-form/contact-lookup-form.component.ts